// Vocal track drawer: every lane's name, color, volume, pan, mute/solo
// and FX. Lanes and waveforms stay on the timeline when this closes.
import React, { useEffect, useRef, useState } from "react";
import { Plus, X, Wand2 } from "lucide-react";
import { LANE_PALETTE, laneColorTokens } from "./WaveformLane";

const panLabel = (p) => (p === 0 ? "C" : p < 0 ? `L${Math.round(-p * 100)}` : `R${Math.round(p * 100)}`);

const Row = ({ t, idx, editable, onPatch, onFx, highlight }) => {
  const [name, setName] = useState(t.label || `Vocal ${idx + 1}`);
  const ref = useRef(null);
  useEffect(() => { if (highlight) ref.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [highlight]);
  const tok = laneColorTokens(t.color);
  const toggle = (on, cls) => `h-7 px-2 rounded text-[10px] font-black border ${on ? cls : "border-white/15 text-gray-300"}`;
  return (
    <li ref={ref} className={`p-2 rounded-lg border ${highlight ? "bg-fuchsia-500/10 border-fuchsia-400/70" : "bg-black/30 border-white/10"}`}
      data-testid={`vocal-drawer-row-${t.track_id}`} data-selected={highlight ? "1" : "0"}>
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-7 rounded-sm shrink-0" style={{ background: `#${tok.hex}` }} />
        <input value={name} disabled={!editable} maxLength={40} onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() && name !== t.label && onPatch(t.track_id, { label: name.trim() })}
          className="flex-1 min-w-0 bg-transparent border border-white/10 rounded px-2 h-7 text-xs text-white disabled:opacity-60"
          data-testid={`vocal-drawer-name-${t.track_id}`} aria-label="Track name" />
        <button type="button" onClick={() => onPatch(t.track_id, { mute: !t.mute })} className={toggle(t.mute, "bg-red-500/80 border-red-300 text-white")}
          data-testid={`vocal-drawer-mute-${t.track_id}`}>M</button>
        <button type="button" onClick={() => onPatch(t.track_id, { solo: !t.solo })} className={toggle(t.solo, "bg-amber-400 border-amber-200 text-black")}
          data-testid={`vocal-drawer-solo-${t.track_id}`}>S</button>
        <button type="button" onClick={() => onFx(t.track_id)} className={toggle(false, "")} data-testid={`vocal-drawer-fx-${t.track_id}`}
          aria-label="Vocal effects"><Wand2 className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid grid-cols-[auto_1fr_auto_4.5rem_auto] items-center gap-1.5 mt-1.5 text-[9px] text-gray-400">
        <span>Vol</span>
        <input type="range" min={-24} max={6} step={0.5} value={t.gain_db || 0} disabled={!editable}
          onChange={(e) => onPatch(t.track_id, { gain_db: parseFloat(e.target.value) })}
          className="accent-fuchsia-400" aria-label="Track volume" data-testid={`vocal-drawer-gain-${t.track_id}`} />
        <span>Pan</span>
        <input type="range" min={-1} max={1} step={0.05} value={t.pan || 0} disabled={!editable}
          onChange={(e) => onPatch(t.track_id, { pan: parseFloat(e.target.value) })}
          className="accent-sky-400" aria-label="Pan" data-testid={`vocal-drawer-pan-${t.track_id}`} />
        <span className="w-6 tabular-nums">{panLabel(t.pan || 0)}</span>
      </div>
      {editable && (
        <div className="flex gap-1.5 mt-1.5" data-testid={`vocal-drawer-colors-${t.track_id}`}>
          {Object.keys(LANE_PALETTE).map((c) => (
            <button key={c} type="button" onClick={() => onPatch(t.track_id, { color: c })} aria-label={`Color ${c}`}
              className={`w-5 h-5 rounded-full ${LANE_PALETTE[c].swatch} ${t.color === c ? "ring-2 ring-white ring-offset-1 ring-offset-black" : "opacity-60"}`}
              data-testid={`vocal-drawer-color-${t.track_id}-${c}`} />
          ))}
        </div>
      )}
    </li>
  );
};

export default function VocalTrackDrawer({ open, onClose, tracks, max, onAdd, onPatch, onFx, canWrite, busy, highlightId }) {
  if (!open) return null;
  const full = tracks.length >= max;
  return (
    <>
      <div className="fixed inset-0 z-30 bg-black/40" onClick={onClose} />
      <aside className="fixed top-0 bottom-[76px] right-0 z-40 w-[21rem] max-w-[92vw] bg-[#0a0a10] border-l border-white/10 flex flex-col"
        data-testid="vocal-track-drawer" role="dialog" aria-label="Vocal tracks">
        <header className="flex items-center px-4 h-12 border-b border-white/10">
          <p className="text-xs font-bold flex-1">Vocal Tracks <span className="text-gray-500">{tracks.length}/{max}</span></p>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white" data-testid="vocal-drawer-close"><X className="w-4 h-4" /></button>
        </header>
        <div className="p-4 flex-1 overflow-y-auto space-y-3">
          <button type="button" disabled={full || busy} onClick={() => onAdd()}
            className="w-full h-10 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-40"
            data-testid="vocal-add-btn"><Plus className="w-4 h-4" />Add Vocal {tracks.length + 1}</button>
          {full && <p className="text-[10px] text-gray-500" data-testid="studio-max-vocals">Max {max} vocal tracks per session</p>}
          {tracks.length === 0 ? <p className="text-[11px] text-gray-500">No vocal tracks yet.</p> : (
            <ul className="space-y-1.5">
              {tracks.map((t, i) => <Row key={`${t.track_id}-${t.label}`} t={t} idx={i} editable={canWrite(t.track_id)} onPatch={onPatch} onFx={onFx} highlight={t.track_id === highlightId} />)}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
}
